import "./business-analytics.css";
import { useNavigate } from "react-router-dom";
import useLocalizedPath from "../../../components/use-localized-path";
import { useTranslation } from "react-i18next";
import Timeline from "../../../components/timeline/timeline-comp";

export default function BusinessAnalytics() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const localize = useLocalizedPath();
  const goToContact = () => {
    navigate(localize("/contact-us"));
  };

  const goToAbout = () => {
    navigate(localize("/about-us"));
  };

  const timelineData = [
    {
      date: "01",
      title: "Actionable Insights",
      content:
        "Transform raw data into visual, easy-to-understand insights that drive decision-making.",
    },
    {
      date: "02",
      title: "Enhanced Collaboration",
      content:
        "Integrated dashboards allow cross-functional teams to collaborate and share insights effortlessly.",
    },
    {
      date: "03",
      title: "Strategic Forecasting",
      content:
        "Predictive analytics enable businesses to foresee market trends, optimize inventory levels, and anticipate customer needs.",
    },
    {
      date: "04",
      title: "Competitive Advantage",
      content:
        "Stay ahead of the competition with real-time insights and a data-driven approach to business strategy.",
    },
    {
      date: "05",
      title: "Improved Customer Understanding",
      content:
        "Analyze customer behavior and preferences, enabling more targeted and effective marketing strategies.",
    },
  ];

  const challenges = [
    {
      number: "01",
      text: "Challenges in extracting valuable insights from large volumes of data.",
    },
    {
      number: "02",
      text: "Difficulty in correlating data, predicting trends, and making informed decisions.",
    },
    {
      number: "03",
      text: "Business inefficiency and missed opportunities without proper analytics.",
    },
  ];

  const cards = [
    {
      icon: "fa-solid fa-chart-pie", // Represents dashboards, data visualization
      title: "Innovative Dashboarding Tools",
      text: "Our solutions offer interactive dashboards that enable instant access to key metrics, visualizations, and insights.",
    },
    {
      icon: "fa-solid fa-database", // Represents data, BI platforms, and analysis
      title: "Powerful Business Intelligence Platforms",
      text: "Integrating tools like Tableau, Power BI, and QlikView for in-depth analysis and reporting.",
    },
    {
      icon: "fa-solid fa-brain", // Represents AI, ML, and predictive analytics
      title: "Predictive and Prescriptive Analytics",
      text: "Utilizing machine learning to predict future trends and provide recommendations for business strategies.",
    },
  ];

  return (
    <div className="main-container">
      {/* main image section */}
      <section className="main-image-section business-analytics-section">
        <div className="main-image-dark-effect">
          <p className="sub-container-sub-heading">{t("Services")}</p>
          <h1 className="sub-container-heading">{t("Business Analytics")}</h1>
          <p className="sub-container-desc">
            {t("Unlock the power of data with intelligent analytics solutions that drive smarter decisions, improve efficiency, and uncover growth opportunities through real-time insights and predictive intelligence.")}
          </p>
          <div className="main-img-container-buttons">
            <button
              type="button"
              className="img-contact-btn common-btn-styles"
              onClick={goToContact}
            >
              {t("Contact Us")}
            </button>
            <button
              type="button"
              className="img-about-btn common-btn-styles"
              onClick={goToAbout}
            >
              {t("About Us")}
            </button>
          </div>
        </div>
      </section>

      {/* service overview section */}
      <section className="service-overview">
        <div className="service-overview-width sub-container">
          <div>
            <h2 className="sub-container-heading">{t("Service Overview")}</h2>
          </div>

          <div className="service-img-container">
            <div className="service-image">
              <img
                src="/assets/images/business-analytics-service-overview.webp"
                alt={t("Business Analytics Overview")}
              />
            </div>

            {/* Right Content */}
            <div className="service-content">
              <p>
                {t("Our Business Analytics services help organizations turn data into actionable insights. We enable smarter decisions through advanced analytics, AI-driven insights, and real-time dashboards to boost performance and predict trends effectively.")}
              </p>
              <ul>
                <li>
                  <i className="fa-solid fa-angles-right"></i>
                  {t("Range of analytics solutions designed to drive business intelligence.")}
                </li>
                <li>
                  <i className="fa-solid fa-angles-right"></i>
                  {t("Utilizing advanced methodologies to interpret complex data across the business lifecycle.")}
                </li>
                <li>
                  <i className="fa-solid fa-angles-right"></i>
                 {t("Expertise in delivering actionable insights for robust performance and strategic growth.")}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/*challenges secton*/}
      <section className="challenges-section sub-container">
        <h2 className="sub-container-heading">{t("Challenges")}</h2>
        <p className="sub-container-desc">
          {t("Common business analytics challenges organizations face in today’s digital landscape.")}
        </p>

        <div className="challenges-container">
          {challenges.map((item, index) => (
            <div
              className="challenge-box"
              key={index}
              data-number={item.number}
            >
              <p className="challenge-text">{t(item.text)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* how are we solving section */}
      <section className="solving-section">
        <div className="solving-section-container sub-container">
          <h2 className="sub-container-heading">{t("How Are We Solving")}</h2>
          <p className="sub-container-desc">
            {t("We combine strategic insight, data expertise, and collaboration to deliver business analytics solutions that empower organizations to unlock insights, enhance decision-making, and drive growth through data-driven innovation.")}
          </p>

          <div className="solving-cards">
            {cards.map((card, index) => (
              <div className="solving-card" key={index}>
                <div className="icon-wrapper">
                  <i className={card.icon}></i>
                </div>
                <h3>{t(card.title)}</h3>
                <p>{t(card.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* benefits section */}
      <section className="benefits-section sub-container">
        <h2 className="sub-container-heading">{t("Benefits")}</h2>
        <p className="sub-container-desc business-analytics-desc">
          {t("With the quality and approach, we would be delivering the best of what you’d need in consulting, suggesting, providing right solutions thus yielding best benefits for your organizations productivity.")}
        </p>
        <div>
          <Timeline timelineData={timelineData} />
        </div>
      </section>
    </div>
  );
}
